import React from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaMicrosoft, FaCloud, FaCode, FaLaptopCode } from 'react-icons/fa';
import './Home.css';

const Home = () => {
  return (
    <>
      {/* Hero Section */}
      <section className="hero">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="hero-content">
              <motion.h1 
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="hero-title"
              >
                Ajyendra Singh
              </motion.h1>
              <motion.p 
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="hero-subtitle"
              >
                Microsoft Dynamics 365 & Power Platform Expert
              </motion.p>
              <motion.div 
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="hero-description"
              >
                <p>With 8+ years of experience in customization, configuration, and implementation of Microsoft Dynamics 365 and Power Platform solutions for diverse industries.</p>
              </motion.div>
              <motion.div 
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
                className="hero-buttons"
              >
                <Link to="/contact">
                  <Button variant="primary" className="btn btn-primary me-3">Get In Touch</Button>
                </Link>
                <Link to="/projects">
                  <Button variant="outline-primary" className="btn btn-outline">View Projects</Button>
                </Link>
              </motion.div>
            </Col>
            <Col lg={6} className="hero-image">
              <motion.img 
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                src="https://via.placeholder.com/600x600.png?text=Ajyendra+Singh" 
                alt="Ajyendra Singh" 
                className="img-fluid"
              />
            </Col>
          </Row>
        </Container>
      </section>

      {/* Expertise Section */}
      <section className="expertise">
        <Container>
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="section-title"
          >
            My Expertise
          </motion.h2>
          <Row>
            <Col md={6} lg={3} className="mb-4">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="expertise-card"
              >
                <div className="expertise-icon">
                  <FaMicrosoft />
                </div>
                <h3>Microsoft Dynamics 365</h3>
                <p>Expert in customization and configuration of Dynamics 365 Customer Engagement applications.</p>
              </motion.div>
            </Col>
            <Col md={6} lg={3} className="mb-4">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="expertise-card"
              >
                <div className="expertise-icon">
                  <FaCloud />
                </div>
                <h3>Power Platform</h3>
                <p>Proficient in Power Apps, Power Automate, Power BI, and developing custom solutions.</p>
              </motion.div>
            </Col>
            <Col md={6} lg={3} className="mb-4">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="expertise-card"
              >
                <div className="expertise-icon">
                  <FaCode />
                </div>
                <h3>Custom Development</h3>
                <p>Experienced in C#, JavaScript, Plugins, PCF Controls, and Azure integrations.</p>
              </motion.div>
            </Col>
            <Col md={6} lg={3} className="mb-4">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="expertise-card"
              >
                <div className="expertise-icon">
                  <FaLaptopCode />
                </div>
                <h3>Cloud Solutions</h3>
                <p>Building solutions with Azure Functions, Logic Apps, API Management and App Services.</p>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Certifications Section */}
      <section className="certifications">
        <Container>
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="section-title"
          >
            Microsoft Certifications
          </motion.h2>
          <Row className="justify-content-center">
            <Col md={6} lg={3} className="mb-4">
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="cert-card"
              >
                <div className="cert-badge">
                  <img src="https://via.placeholder.com/150x150.png?text=PL-600" alt="PL-600" className="img-fluid" />
                </div>
                <h4>PL-600</h4>
                <p>Microsoft Power Platform Solution Architect</p>
              </motion.div>
            </Col>
            <Col md={6} lg={3} className="mb-4">
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="cert-card"
              >
                <div className="cert-badge">
                  <img src="https://via.placeholder.com/150x150.png?text=PL-400" alt="PL-400" className="img-fluid" />
                </div>
                <h4>PL-400</h4>
                <p>Microsoft Power Platform Developer Associate</p>
              </motion.div>
            </Col>
            <Col md={6} lg={3} className="mb-4">
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="cert-card"
              >
                <div className="cert-badge">
                  <img src="https://via.placeholder.com/150x150.png?text=PL-100" alt="PL-100" className="img-fluid" />
                </div>
                <h4>PL-100</h4>
                <p>Microsoft Power Platform App Maker</p>
              </motion.div>
            </Col>
            <Col md={6} lg={3} className="mb-4">
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="cert-card"
              >
                <div className="cert-badge">
                  <img src="https://via.placeholder.com/150x150.png?text=PL-900" alt="PL-900" className="img-fluid" />
                </div>
                <h4>PL-900</h4>
                <p>Microsoft Power Platform Fundamentals</p>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Featured Projects Section */}
      <section className="featured-projects">
        <Container>
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="section-header"
          >
            <h2 className="section-title">Featured Projects</h2>
            <Link to="/projects" className="view-all">
              View All Projects <i className="fas fa-arrow-right"></i>
            </Link>
          </motion.div>
          <Row>
            <Col lg={6} className="mb-4">
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="project-card"
              >
                <div className="project-image">
                  <img src="https://via.placeholder.com/600x400.png?text=Baringa+Project" alt="Baringa Project" className="img-fluid" />
                </div>
                <div className="project-content">
                  <h3>Baringa Projects Management</h3>
                  <p>Collaborated with stakeholders to design and develop scalable solutions for ProjOps CRM.</p>
                  <div className="tech-stack">
                    <span className="tech-badge">Microsoft Dynamics 365</span>
                    <span className="tech-badge">Power Platform</span>
                    <span className="tech-badge">JavaScript</span>
                  </div>
                </div>
              </motion.div>
            </Col>
            <Col lg={6} className="mb-4">
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="project-card"
              >
                <div className="project-image">
                  <img src="https://via.placeholder.com/600x400.png?text=Costa+Franchise+Portal" alt="Costa Franchise Portal" className="img-fluid" />
                </div>
                <div className="project-content">
                  <h3>Costa Franchise Portal</h3>
                  <p>Managed technical design and deployment for UK's second largest coffee industry franchise portal.</p>
                  <div className="tech-stack">
                    <span className="tech-badge">Powerapps Portal</span>
                    <span className="tech-badge">Azure Functions</span>
                    <span className="tech-badge">Power Automate</span>
                  </div>
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Call to Action */}
      <section className="cta">
        <Container>
          <Row className="justify-content-center text-center">
            <Col lg={8}>
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <h2>Ready to Work Together?</h2>
                <p>Let's discuss how I can help with your Microsoft Dynamics 365 and Power Platform projects.</p>
                <Link to="/contact">
                  <Button variant="primary" size="lg" className="btn btn-primary mt-3">Get In Touch</Button>
                </Link>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default Home;